import { useState, useCallback, useMemo } from 'react';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { formatTableTimestamp } from '../utils/formatTableDate';
import { Button } from '@predoc/cow';
import { DataTable, TableSkeleton, SortableHeader } from '@/components';
import { NonMedicalDocument } from './types';
import type { DocumentViewerData } from '../../PatientProfile';

interface NonMedicalDocumentsTableProps {
  documents: NonMedicalDocument[];
  isLoading: boolean;
  onViewDocument: (document: DocumentViewerData) => void;
}

export const NonMedicalDocumentsTable = ({
  documents,
  isLoading,
  onViewDocument,
}: NonMedicalDocumentsTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const handleView = useCallback(
    (document: NonMedicalDocument) => {
      const firstImage = document.images.find((img) => img.signedUrl);
      if (firstImage) {
        onViewDocument({ url: firstImage.signedUrl, title: document.name });
      }
    },
    [onViewDocument],
  );

  const columns = useMemo<ColumnDef<NonMedicalDocument>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Document',
        cell: ({ row }) => row.original.name || 'No data available',
      },
      {
        accessorKey: 'createdAt',
        enableSorting: true,
        header: ({ column }) => (
          <SortableHeader column={column}>Date Uploaded</SortableHeader>
        ),
        cell: ({ row }) => formatTableTimestamp(row.original.createdAt),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const actionEnabled = row.original.images.some(
            (img) => img.signedUrl,
          );
          return (
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!actionEnabled}
                onClick={() => handleView(row.original)}
                aria-label={`View ${row.original.name}`}
              >
                View
              </Button>
            </div>
          );
        },
      },
    ],
    [handleView],
  );

  if (isLoading) {
    return <TableSkeleton rows={5} columns={3} />;
  }

  if (documents.length === 0) {
    return (
      <div className="flex min-h-[300px] flex-col items-center justify-center px-4 py-12">
        <img
          src="/suggested-providers.png"
          alt="No documents to show"
          className="mb-4 max-w-[200px]"
        />
        <h3 className="text-lg font-semibold">No documents to show</h3>
        <p className="text-muted-foreground text-sm">
          Non-medical documents will appear here.
        </p>
      </div>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={documents}
      sorting={sorting}
      onSortingChange={setSorting}
    />
  );
};
